import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Layers, Loader2, AlertTriangle, ArrowLeft, Edit2, Trash2, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';
import { useContextualNavigation } from '../hooks/useContextualNavigation';
import { emitEntityEvent } from '../services/entityEvents';
import { Queue, CircuitBreakerState, CircuitTripEvent, QueueStats } from '../types';
import CircuitBreakerIndicator from '../components/Queue/CircuitBreakerIndicator';
import CircuitBreakerAlert from '../components/Queue/CircuitBreakerAlert';
import QueueCreationModal from '../components/Queue/QueueCreationModal';
import TriggerSummary from '../components/Queue/TriggerSummary';
import Tooltip from '../components/common/Tooltip';

/**
 * QueuesPage - List all queues for a project
 *
 * Route: /projects/:projectId/queues
 *
 * Features:
 * - Display all queues for the project
 * - Show CircuitBreakerIndicator for each queue
 * - Display CircuitBreakerAlert banner when any circuit is tripped
 * - Links to individual queue detail pages
 * - Loading and error states
 */
const QueuesPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { getBackToWorkflow } = useContextualNavigation();
  const backToWorkflow = getBackToWorkflow(projectId);

  // State
  const [queues, setQueues] = useState<Queue[]>([]);
  const [circuitStates, setCircuitStates] = useState<Record<number, CircuitBreakerState>>({});
  const [queueStats, setQueueStats] = useState<Record<number, QueueStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<number>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingQueue, setDeletingQueue] = useState<Queue | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // AC-1326: background refresh so the initial loading skeleton doesn't
  // flash on every manual refresh click.
  const [refreshing, setRefreshing] = useState(false);

  // AC-1417: sort state. Default is name ascending so the page lands in a
  // predictable alphabetical order rather than creation order.
  type SortField = 'name' | 'status' | 'trigger' | 'items' | 'created';
  const [sortConfig, setSortConfig] = useState<{ field: SortField; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });

  const handleSort = (field: SortField) => {
    setSortConfig(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    );
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) return <div className="w-4 h-4" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  // Status sort key: rank tripped circuits first when descending so an
  // operator clicking "Status" desc surfaces problems at the top.
  const statusRank = (queueId: number): number => {
    const s = circuitStates[queueId]?.state;
    if (s === 'OPEN') return 0;
    return 2;
  };

  // Trigger sort key: enabled with an interval ranks earliest, disabled last.
  const triggerRank = (queue: Queue): number => {
    if (!queue.auto_trigger_enabled) return 3;
    if (queue.trigger_interval_minutes != null) return queue.trigger_interval_minutes;
    if (queue.trigger_item_threshold != null) return 100000;
    return 200000;
  };

  const itemsCount = (queueId: number): number => {
    const s = queueStats[queueId];
    if (!s) return 0;
    return (s.pending || 0) + (s.processing || 0) + (s.completed || 0) + (s.failed || 0);
  };

  const sortedQueues = useMemo(() => {
    const arr = [...queues];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortConfig.field) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = statusRank(a.queue_id) - statusRank(b.queue_id);
          break;
        case 'trigger':
          cmp = triggerRank(a) - triggerRank(b);
          break;
        case 'items':
          cmp = itemsCount(a.queue_id) - itemsCount(b.queue_id);
          break;
        case 'created': {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
          cmp = at - bt;
          break;
        }
      }
      if (cmp === 0) cmp = a.name.localeCompare(b.name);
      return cmp * dir;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queues, sortConfig, circuitStates, queueStats]);

  // Fetch queues and circuit states (extracted to be reusable)
  // AC-1326: opts.background skips the full-page skeleton so the table
  // stays visible while a manual refresh is in flight.
  const fetchQueuesAndStates = async (opts: { background?: boolean } = {}) => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    if (opts.background) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch queues
      const fetchedQueues = await apiService.getQueues(projectId);
      setQueues(fetchedQueues);

      // Fetch circuit breaker state and stats for each queue
      const statePromises = fetchedQueues.map(async (queue) => {
        try {
          const state = await apiService.getCircuitBreakerState(queue.queue_id);
          return { queueId: queue.queue_id, state };
        } catch (err) {
          // If fetching circuit state fails, return null state (graceful degradation)
          console.error(`Failed to fetch circuit state for queue ${queue.queue_id}:`, err);
          return { queueId: queue.queue_id, state: null };
        }
      });

      const statsPromises = fetchedQueues.map(async (queue) => {
        try {
          const stats = await apiService.getQueueStats(queue.queue_id);
          return { queueId: queue.queue_id, stats };
        } catch (err) {
          // If fetching stats fails, return null (graceful degradation)
          console.error(`Failed to fetch stats for queue ${queue.queue_id}:`, err);
          return { queueId: queue.queue_id, stats: null };
        }
      });

      const [stateResults, statsResults] = await Promise.all([
        Promise.all(statePromises),
        Promise.all(statsPromises),
      ]);

      const statesMap: Record<number, CircuitBreakerState> = {};
      stateResults.forEach(({ queueId, state }) => {
        if (state) {
          statesMap[queueId] = state;
        }
      });
      setCircuitStates(statesMap);

      const statsMap: Record<number, QueueStats> = {};
      statsResults.forEach(({ queueId, stats }) => {
        if (stats) {
          statsMap[queueId] = stats;
        }
      });
      setQueueStats(statsMap);
    } catch (err) {
      console.error('Failed to load queues:', err);
      setError('Failed to load queues. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch queues and circuit states on mount
  useEffect(() => {
    fetchQueuesAndStates();
  }, [projectId]);

  // Get trip events for queues with OPEN circuits (for alert display)
  const tripEvents: CircuitTripEvent[] = queues
    .filter((queue) => {
      const state = circuitStates[queue.queue_id];
      return state?.state === 'OPEN' && !dismissedAlerts.has(queue.queue_id);
    })
    .map((queue) => ({
      queue_id: queue.queue_id,
      queue_name: queue.name,
      reason: queue.circuit_open_reason || circuitStates[queue.queue_id]?.reason || 'Unknown',
      last_error_message: undefined,
      opened_at: queue.circuit_opened_at || circuitStates[queue.queue_id]?.opened_at || new Date().toISOString(),
    }));

  // Handle dismissing an alert
  const handleDismissAlert = (queueId: number) => {
    setDismissedAlerts((prev) => {
      const newSet = new Set(prev);
      newSet.add(queueId);
      return newSet;
    });
  };

  // Handle queue created
  const handleQueueCreated = async (queue: Queue) => {
    // Close modal
    setIsModalOpen(false);
    // Refresh queue list
    await fetchQueuesAndStates();
  };

  // Handle queue delete
  const handleDeleteConfirm = async () => {
    if (!deletingQueue) return;
    const deletedId = deletingQueue.queue_id;
    try {
      setDeleteLoading(true);
      await apiService.deleteQueue(deletedId);
      // AC-1369: notify any open WorkflowEditor / queue node that's referencing
      // this queue so it can clear or re-fetch its cached display, instead of
      // showing the now-deleted queue name until a coincidental re-render.
      emitEntityEvent({ entity: 'queue', kind: 'deleted', id: deletedId });
      setDeletingQueue(null);
      await fetchQueuesAndStates();
    } catch (err) {
      console.error('Failed to delete queue:', err);
      setError('Failed to delete queue. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading queues...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-red-700 dark:text-red-300">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Back navigation */}
      <div className="mb-4">
        {backToWorkflow ? (
          <Link
            to={backToWorkflow.path}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {backToWorkflow.label}
          </Link>
        ) : projectId && (
          <Link
            to={`/projects/${projectId}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Overview
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Queues</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage queues and circuit breaker settings for your project
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* AC-1326: Manual refresh so operators don't have to reload
              the browser after running retry/trip/reset actions. */}
          <button
            data-testid="refresh-queues-button"
            onClick={() => fetchQueuesAndStates({ background: true })}
            disabled={refreshing || loading}
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Refresh queues"
            title="Refresh queues"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="ml-2 sm:hidden">Refresh</span>
          </button>
          <button
            data-testid="create-queue-button"
            onClick={() => setIsModalOpen(true)}
            className="flex-1 sm:flex-none px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[48px]"
          >
            + Create Queue
          </button>
        </div>
      </div>

      {/* Circuit Breaker Alert - Show first tripped circuit */}
      {tripEvents.length > 0 && (
        <CircuitBreakerAlert
          tripEvent={tripEvents[0]}
          projectId={projectId}
          onDismiss={() => handleDismissAlert(tripEvents[0].queue_id)}
        />
      )}

      {/* Empty state */}
      {queues.length === 0 && (
        <div className="text-center py-12">
          <Layers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">No queues found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            There are currently no message queues configured for this project.
          </p>
          <button
            data-testid="create-first-queue-button"
            onClick={() => setIsModalOpen(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Create your first queue
          </button>
        </div>
      )}

      {/* Queue list */}
      {queues.length > 0 && (
        <div className="bg-white dark:bg-cap-dark shadow rounded-lg overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">Name {getSortIcon('name')}</div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">Status {getSortIcon('status')}</div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('trigger')}
                >
                  <div className="flex items-center gap-1">Trigger {getSortIcon('trigger')}</div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('items')}
                >
                  <div className="flex items-center gap-1">Items {getSortIcon('items')}</div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => handleSort('created')}
                >
                  <div className="flex items-center gap-1">Created {getSortIcon('created')}</div>
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
              {sortedQueues.map((queue) => (
                <tr key={queue.queue_id} className="hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                          <Layers className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        {/* AC-1313: description moved from its own column into a
                            tooltip on the queue name so we can show the new
                            Trigger column without horizontal scroll. */}
                        <Tooltip content={queue.description || 'No description'}>
                          <Link
                            to={`/projects/${projectId}/queues/${queue.queue_id}`}
                            className="text-sm font-medium text-primary-600 hover:text-primary-800"
                          >
                            {queue.name}
                          </Link>
                        </Tooltip>
                        {queue.priority_field && (
                          <div className="mt-0.5">
                            <Tooltip content={`Priority by "${queue.priority_field}" (highest first)`}>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                Priority: <code className="ml-1 font-mono">{queue.priority_field}</code>
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* AC-1418: Inactive badge so the list reflects the
                          operator-level pause toggle next to the circuit
                          breaker indicator. */}
                      {queue.is_enabled === false && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          Inactive
                        </span>
                      )}
                      <CircuitBreakerIndicator
                        queueId={queue.queue_id}
                        circuitState={circuitStates[queue.queue_id]}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <TriggerSummary queue={queue} />
                  </td>
                  <td className="px-6 py-4">
                    {queueStats[queue.queue_id] ? (
                      <div className="flex flex-wrap gap-1">
                        {queueStats[queue.queue_id].pending > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            {queueStats[queue.queue_id].pending} pending
                          </span>
                        )}
                        {queueStats[queue.queue_id].processing > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {queueStats[queue.queue_id].processing} processing
                          </span>
                        )}
                        {queueStats[queue.queue_id].completed > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            {queueStats[queue.queue_id].completed} completed
                          </span>
                        )}
                        {queueStats[queue.queue_id].failed > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            {queueStats[queue.queue_id].failed} failed
                          </span>
                        )}
                        {/* AC-1315 / AC-1318: always surface the parent's
                            error-queue depth when an error queue exists.
                            Red when > 0 so failures stand out; neutral gray
                            when 0 so healthy queues show an explicit "no
                            errors" confirmation instead of silence. */}
                        {queue.error_queue_id != null && (
                          <Tooltip
                            content={
                              (queue.error_queue_item_count ?? 0) > 0
                                ? `${queue.error_queue_item_count} items failed max_retry_count attempts and were moved to the error queue. Click to triage.`
                                : `Error queue is empty. Items that fail max_retry_count attempts land here.`
                            }
                          >
                            <Link
                              to={`/projects/${projectId}/queues/${queue.error_queue_id}`}
                              className={
                                (queue.error_queue_item_count ?? 0) > 0
                                  ? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
                                  : 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }
                              onClick={(e) => e.stopPropagation()}
                            >
                              {queue.error_queue_item_count ?? 0} errors
                            </Link>
                          </Tooltip>
                        )}
                        {queueStats[queue.queue_id].total === 0 &&
                          queue.error_queue_id == null && (
                          <span className="text-xs text-gray-400">No items</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {queue.created_at
                        ? new Date(queue.created_at).toLocaleDateString()
                        : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/projects/${projectId}/queues/${queue.queue_id}`)}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        aria-label={`Edit ${queue.name}`}
                        title="Edit queue"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletingQueue(queue)}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        aria-label={`Delete ${queue.name}`}
                        title="Delete queue"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Queue Creation Modal */}
      <QueueCreationModal
        projectId={projectId!}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onQueueCreated={handleQueueCreated}
      />

      {/* Delete Confirmation Dialog */}
      {deletingQueue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
              Delete Queue
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-warm-white">{deletingQueue.name}</span>? This will also delete its error queue and all items in both queues. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingQueue(null)}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Queue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueuesPage;
